import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  AvailabilityDataService,
  AvailabilityRequest,
  AvailabilityResponse,
  MaterialAvailability,
} from '@vi-succ/availability';
import dayjs from 'dayjs';
import { Observable, combineLatest, filter, first, map, of, switchMap } from 'rxjs';
import { AuthService } from '../core/login/services/auth.service';
import { selectLoading, selectUserContextInfo } from '../core/login/state';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class EtAvailabilityDataService implements AvailabilityDataService {
  constructor(private http: HttpClient, private store: Store, private authService: AuthService) {}

  getAvailability(request: AvailabilityRequest): Observable<MaterialAvailability[]> {
    return this.authService.authenticated$.pipe(
      switchMap((authenticated) => {
        if (!authenticated) {
          return of([]);
        } else {
          return combineLatest([this.store.select(selectUserContextInfo), this.store.select(selectLoading)]).pipe(
            filter(([, loading]) => !loading),
            first(),
            switchMap(([contextInfo, loading]) => {
              if (!contextInfo && !loading) {
                return of([]);
              } else {
                // We cheat for now to use 0501 as storage location for employees
                // TODO: Change to a specific mapping
                const deliveryPlant =
                  (contextInfo?.salesOrgs ?? []).find((salesOrg) => salesOrg.salesOrg === request.salesOrg)
                    ?.deliveringPlant ??
                  contextInfo?.activeSalesOrg?.deliveringPlant ??
                  '0501';

                // Find next working day
                const nextWorkingDay = this.nextWorkingDay();
                const req: AvailabilityRequest = {
                  ...request,
                  locationKeys: [{ storage: deliveryPlant }],
                  deliveryDate: nextWorkingDay.format('YYYY-MM-DD'),
                };
                return this.http
                  .post<AvailabilityResponse>(`${environment.backend.url}/product`, req, {
                    headers: {
                      'X-API-KEY': environment.backend.apiKey,
                    },
                  })
                  .pipe(
                    map((response) => {
                      if (response.locationInfos.length > 0) {
                        return response.locationInfos[0].materialAvailabilityInfos;
                      }
                      return [];
                    })
                  );
              }
            })
          );
        }
      })
    );
  }

  getAvailabilityForLocations(request: AvailabilityRequest): Observable<AvailabilityResponse> {
    return this.store.select(selectUserContextInfo).pipe(
      first(),
      switchMap((contextInfo) => {
        if (!contextInfo) {
          return of({ locationInfos: [] });
        }

        // Find next working day
        const nextWorkingDay = this.nextWorkingDay();
        const req: AvailabilityRequest = {
          ...request,
          deliveryDate: nextWorkingDay.format('YYYY-MM-DD'),
        };
        return this.http.post<AvailabilityResponse>(environment.backend.url + '/product', req, {
          headers: {
            'X-API-KEY': environment.backend.apiKey,
          },
        });
      })
    );
  }

  private nextWorkingDay() {
    const today = dayjs();
    const tomorrow = today.add(1, 'day');
    if (tomorrow.day() === 0) {
      return tomorrow.add(1, 'day');
    } else if (tomorrow.day() === 6) {
      return tomorrow.add(2, 'day');
    } else {
      return tomorrow;
    }
  }
}
